import { FocusEvent, MouseEvent, useMemo, useState } from "react";
import { OptionV2 } from "../types";
import { formatErrorMessageV2 } from "../utils";

export type DropdownV2MenuMount = "static" | "overlay";

export const DROPDOWN_V2_CLEAR_VALUE = "__DROPDOWN_V2_CLEAR__";

export type DropdownV2Props<T = any> = {
  id?: string;
  name: string;
  label?: string;
  description?: string;
  value?: OptionV2<T> | null;
  options?: OptionV2<T>[];
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  error?: string;
  emptyMessage?: string;
  menuMount?: DropdownV2MenuMount;
  clearOptionLabel?: string;
  onChange?: (option: OptionV2<T> | null) => void;
  onBlur?: () => void;
};

export function DropdownV2<T = any>({
  id,
  name,
  label,
  description = "",
  value,
  options = [],
  placeholder = "Selecione...",
  disabled = false,
  searchable = false,
  error,
  emptyMessage = "Nenhuma opção disponível",
  menuMount = "static",
  clearOptionLabel,
  onChange,
  onBlur,
}: DropdownV2Props<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selectId = id || `onboarding-v2-${name}`;

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const base = normalizedSearch
      ? options.filter((option) => option.label.toLowerCase().includes(normalizedSearch))
      : options;
    if (!clearOptionLabel) return base;
    const clearOption: OptionV2<T> = {
      label: clearOptionLabel,
      value: DROPDOWN_V2_CLEAR_VALUE as unknown as T,
      info: { __clearSelection: true },
    };
    return [clearOption, ...base];
  }, [options, search, clearOptionLabel]);

  function isClearOption(option: OptionV2<T>) {
    return Boolean(clearOptionLabel) && String(option.value) === DROPDOWN_V2_CLEAR_VALUE;
  }

  function handleToggle() {
    if (!disabled) setIsOpen((current) => !current);
  }

  function handleSelect(option: OptionV2<T>) {
    if (isClearOption(option)) {
      onChange && onChange(null);
    } else {
      onChange && onChange(option);
    }
    setIsOpen(false);
    setSearch("");
  }

  function handleOptionMouseDown(event: MouseEvent<HTMLButtonElement>) {
    // Safari may fire blur with a null relatedTarget before the option click.
    // Preserve focus until the click selects the option and closes the menu.
    event.preventDefault();
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setIsOpen(false);
    setSearch("");
    onBlur && onBlur();
  }

  const menuClassName =
    menuMount === "overlay"
      ? "ov2-dropdown__menu ov2-dropdown__menu--overlay"
      : "ov2-dropdown__menu";

  const trigger = (
    <button
      className={`ov2-dropdown__trigger ${isOpen ? "ov2-dropdown__trigger--open" : ""} ${error ? "ov2-input--error" : ""} ${disabled ? "ov2-input--disabled" : ""}`}
      type="button"
      onClick={handleToggle}
      disabled={disabled}
    >
      <span className={value ? "ov2-dropdown__value" : "ov2-dropdown__placeholder"}>
        {value?.label || placeholder}
      </span>
      {!disabled && (
        <span className={`ov2-dropdown__chevron ${isOpen ? "ov2-dropdown__chevron--open" : ""}`}>⌃</span>
      )}
    </button>
  );

  function renderMenu() {
    if (!isOpen) return null;
    return (
      <div className={menuClassName}>
        {searchable && (
          <input
            className="ov2-dropdown__search"
            value={search}
            placeholder="Buscar..."
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
        )}
        <ul>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <li key={`${option.value}`}>
                <button
                  type="button"
                  onMouseDown={handleOptionMouseDown}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </button>
              </li>
            ))
          ) : (
            <li className="ov2-dropdown__empty">{emptyMessage}</li>
          )}
        </ul>
      </div>
    );
  }

  return (
    <div className="ov2-field ov2-dropdown" id={selectId} onBlur={handleBlur}>
      {label && <span className="ov2-field__label">{label}</span>}
      {description && <span className="ov2-field__description">{description}</span>}
      {menuMount === "overlay" ? (
        <div className="ov2-dropdown__anchor">
          {trigger}
          {renderMenu()}
        </div>
      ) : (
        <>
          {trigger}
          {renderMenu()}
        </>
      )}
      {error && <small className="ov2-field__error animation__fadeIn">{formatErrorMessageV2(error)}</small>}
    </div>
  );
}
